import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, FileText, MapPin, Users, Clock, X, Plus } from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, addDays, startOfWeek, endOfWeek, getDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarEvent } from '../types/calendar';
import { eventColors } from '../types/eventColor';

interface CalendarProps {
  events?: CalendarEvent[];
  onDateSelect?: (date: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onAddEvent?: (date: Date) => void;
}

export const Calendar: React.FC<CalendarProps> = ({
  events = [],
  onDateSelect,
  onEventClick,
  onAddEvent
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showDateDetails, setShowDateDetails] = useState(false);
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');

  // For month view, we need to get all days in the month plus padding days
  const getMonthDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // Start on Monday
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 }); // End on Sunday
    
    return eachDayOfInterval({ start: startDate, end: endDate });
  };
  
  // For week view
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Start on Monday
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 }); // End on Sunday
  
  // Get days based on view mode
  const getDaysToDisplay = () => {
    if (viewMode === 'month') {
      return getMonthDays();
    } else if (viewMode === 'week') {
      return eachDayOfInterval({ start: weekStart, end: weekEnd });
    } else {
      // Day view just returns the current date
      return [currentDate];
    }
  };
  
  const daysToDisplay = getDaysToDisplay();

  // Get events for a specific date
  const getEventsForDate = (date: Date) => {
    return events.filter(event => isSameDay(new Date(event.date), date));
  };

  // Get event type color
  const getEventTypeColor = (event: CalendarEvent) => {
    const colorKey = event.color || 'blue';
    return eventColors[colorKey];
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    setShowDateDetails(true);
    onDateSelect?.(date);
  };

  const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    onEventClick?.(event);
  };

  const handleAddEventClick = (date: Date, e: React.MouseEvent) => {
    e.stopPropagation();
    onAddEvent?.(date);
  };

  const previousPeriod = () => {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addDays(currentDate, -7));
    } else {
      setCurrentDate(addDays(currentDate, -1));
    }
  };

  const nextPeriod = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addDays(currentDate, 7));
    } else {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get day class names based on its state
  const getDayClassNames = (date: Date) => {
    const baseClasses = "relative hover:bg-gray-50 transition-colors border border-gray-200";
    const isCurrentMonth = isSameMonth(date, currentDate);
    const isSelected = selectedDate && isSameDay(date, selectedDate);
    const isCurrentDay = isToday(date);
    
    // Height classes based on view mode
    const heightClass = viewMode === 'month' ? "h-24" : viewMode === 'week' ? "h-32" : "h-full";
    
    // Width classes based on view mode
    const widthClass = viewMode === 'day' ? "w-full" : "";

    if (!isCurrentMonth && viewMode === 'month') {
      return `${baseClasses} ${heightClass} ${widthClass} bg-gray-50 text-gray-400`;
    }

    if (isSelected) {
      return `${baseClasses} ${heightClass} ${widthClass} bg-blue-50 border-blue-500`;
    }

    if (isCurrentDay) {
      return `${baseClasses} ${heightClass} ${widthClass} bg-yellow-50`;
    }

    return `${baseClasses} ${heightClass} ${widthClass} bg-white`;
  };

  // Render event dots or event cards based on view mode
  const renderEvents = (date: Date, events: CalendarEvent[]) => {
    if (viewMode === 'month') {
      // For month view, show dots
      const maxVisibleDots = 3;
      const hasMoreEvents = events.length > maxVisibleDots;
      const visibleEvents = events.slice(0, maxVisibleDots);
      
      return (
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex items-center gap-1">
          {visibleEvents.map((event, index) => {
            const colorConfig = getEventTypeColor(event);
            return (
              <div
                key={event.id + index}
                className={`w-1.5 h-1.5 rounded-full ${colorConfig.dot}`}
                title={event.title}
              />
            );
          })}
          {hasMoreEvents && (
            <span className="text-xs text-gray-500 ml-1">
              +{events.length - maxVisibleDots}
            </span>
          )}
        </div>
      );
    } else {
      // For week and day view, show event cards
      return (
        <div className="mt-1 space-y-1 max-h-[calc(100%-24px)] overflow-y-auto">
          {events.map((event, index) => {
            const colorConfig = getEventTypeColor(event);
            return (
              <div
                key={event.id + index}
                onClick={(e) => handleEventClick(event, e)}
                className={`px-2 py-1 rounded text-xs truncate cursor-pointer ${colorConfig.bg} ${colorConfig.text} ${colorConfig.hover}`}
                title={event.title}
              >
                <div className="flex items-center gap-1">
                  <span className="font-medium">{format(new Date(event.date), 'HH:mm')}</span>
                  <span className="truncate">{event.title}</span>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  };

  // Generate the grid template based on view mode
  const getGridTemplate = () => {
    if (viewMode === 'month') {
      return 'grid-cols-7';
    } else if (viewMode === 'week') {
      return 'grid-cols-7';
    } else {
      return 'grid-cols-1';
    }
  };

  // Get the header for the current view
  const getViewHeader = () => {
    if (viewMode === 'month') {
      return format(currentDate, 'MMMM yyyy', { locale: de });
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(weekStart, 'd. MMM', { locale: de })} - ${format(weekEnd, 'd. MMM yyyy', { locale: de })}`;
    } else {
      return format(currentDate, 'EEEE, d. MMMM yyyy', { locale: de });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Calendar Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {getViewHeader()}
          </h2>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 transition-colors"
          >
            Heute
          </button>
        </div>
        
        <div className="flex items-center gap-4">
          {/* View mode selector */}
          <div className="flex rounded-md overflow-hidden border border-gray-300">
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1 text-sm ${viewMode === 'month' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            >
              Monat
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1 text-sm ${viewMode === 'week' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            >
              Woche
            </button>
            <button
              onClick={() => setViewMode('day')}
              className={`px-3 py-1 text-sm ${viewMode === 'day' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            >
              Tag
            </button>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={previousPeriod}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Vorheriger Zeitraum"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={nextPeriod}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Nächster Zeitraum"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className={`grid ${getGridTemplate()} gap-px`}>
        {/* Weekday headers - only show for month and week views */}
        {(viewMode === 'month' || viewMode === 'week') && 
          ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
            <div
              key={day}
              className="text-center font-semibold text-gray-600 p-2 bg-gray-50"
            >
              {day}
            </div>
          ))
        }

        {/* Calendar days */}
        {daysToDisplay.map((date) => {
          const dayEvents = getEventsForDate(date);
          const dayNumber = format(date, 'd');
          const dayName = format(date, 'EEEE', { locale: de });
          
          return (
            <div
              key={date.toISOString()}
              className={getDayClassNames(date)}
              onClick={() => handleDateClick(date)}
            >
              <div className="flex justify-between items-start p-2">
                <div className="flex flex-col">
                  {viewMode === 'day' && (
                    <span className="text-sm text-gray-500">{dayName}</span>
                  )}
                  <span className={`text-sm ${
                    isToday(date) ? 'font-bold text-blue-600' : 'text-gray-700'
                  }`}>
                    {dayNumber}
                  </span>
                </div>
                
                {/* Add event button */}
                {onAddEvent && (
                  <button
                    onClick={(e) => handleAddEventClick(date, e)}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                    title="Termin hinzufügen"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Event dots or cards */}
              {dayEvents.length > 0 && renderEvents(date, dayEvents)}
            </div>
          );
        })}
      </div>

      {/* Date Details Modal */}
      {showDateDetails && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <CalendarIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {format(selectedDate, 'EEEE, d. MMMM yyyy', { locale: de })}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {getEventsForDate(selectedDate).length} Termine
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {onAddEvent && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddEvent(selectedDate);
                      setShowDateDetails(false);
                    }}
                    className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    title="Termin hinzufügen"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => setShowDateDetails(false)}
                  className="text-gray-400 hover:text-gray-500 p-2"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Events List */}
            <div className="p-6">
              {getEventsForDate(selectedDate).length === 0 ? (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Keine Termine an diesem Tag</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {getEventsForDate(selectedDate).map((event) => {
                    const colorConfig = getEventTypeColor(event);
                    return (
                      <div
                        key={event.id}
                        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        onClick={(e) => handleEventClick(event, e)}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-lg ${colorConfig.bg}`}>
                            <CalendarIcon className={`h-6 w-6 ${colorConfig.text}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-lg font-medium text-gray-900 truncate">
                              {event.title}
                            </h4>
                            <div className="mt-1 flex flex-wrap gap-4 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                <span>
                                  {format(new Date(event.date), 'HH:mm', { locale: de })}
                                </span>
                              </div>
                              {event.location && (
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-4 w-4" />
                                  <span>{event.location}</span>
                                </div>
                              )}
                              {event.participants && event.participants.length > 0 && (
                                <div className="flex items-center gap-1">
                                  <Users className="h-4 w-4" />
                                  <span>{event.participants.length} Teilnehmer</span>
                                </div>
                              )}
                            </div>
                            {event.description && (
                              <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                                {event.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowDateDetails(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};